import type { ImgHTMLAttributes } from 'react'
import { useMemo } from 'react'

import type { ThumborOptions } from './thumborUrlBuilder'
import { normalizeThumborOptions, urlBuilder } from './thumborUrlBuilder'

export interface ImageOptions extends ImgHTMLAttributes<HTMLImageElement> {
  src: string
  width?: any
  height?: any
  'data-id'?: string
  options?: ThumborOptions
  fetchpriority?: 'high' | 'low' | 'auto'
  ref?: React.MutableRefObject<HTMLImageElement | null>
  /**
   * 'x' (default) generates density descriptors (1x/2x/3x) — the browser
   * picks purely by device pixel ratio, ignoring `sizes`. Use 'w' to
   * generate width descriptors so `sizes` actually drives the pick — needed
   * wherever the rendered box is meaningfully smaller than `width`/`height`
   * (e.g. responsive grid cards) to avoid over-fetching.
   */
  srcSetUnit?: 'x' | 'w'
}

const FACTORS = [1, 2, 3]

export const useImage = ({
  src: baseUrl,
  width,
  height,
  options = {},
  srcSetUnit = 'x',
  ...rest
}: ImageOptions): ImgHTMLAttributes<HTMLImageElement> => {
  const { gallerySrcSet, src } = useMemo(() => {
    const customOptions = normalizeThumborOptions({
      fitIn: true,
      quality: 90,
      format: 'webp',
      ...options,
    })

    const builder = urlBuilder(baseUrl, customOptions)
    const roundedW = Math.round(width || 0)
    const roundedH = Math.round(height || 0)

    const srcSetString = FACTORS.map((factor) => {
      const url = builder(roundedW * factor, roundedH * factor)

      return srcSetUnit === 'w'
        ? `${url} ${roundedW * factor}w`
        : `${url} ${factor}x`
    }).join(', ')

    return {
      src: builder(roundedW, roundedH),
      gallerySrcSet: srcSetString,
    }
  }, [height, options, baseUrl, width, srcSetUnit])

  return {
    src,
    srcSet: gallerySrcSet,
    width,
    height,
    ...rest,
  }
}
